import React, {useContext} from 'react';
import {SubMenuProps} from './SubMenu';
import {MediaType} from '../../../utils/media';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '../../../ui-components/Tabs/Tabs';
import {ColorPicker, useColor} from 'react-color-palette';
import {Button} from '../../../ui-components/Button/button';
import {ColorIcon} from '../../../ui-components/Iconpack';
import {GetTextureFiles} from '../../../components/background-submenu/backgroundMetadata';
import {encodeUrl} from '../../../utils/helper';
import {GlobalStateContext} from '../../../module/pixel-design-studio/context';

export const ColorBgOption = (props: {bgColorPalette?: string; onBgColorSelect: (color: string) => void}) => {
    const [bgColor, setBgColor] = useColor(props.bgColorPalette ? props.bgColorPalette : '#561ecb');

    const presetArray = [
        '#FFFFFF',
        '#000000',
        '#FF4405',
        '#F63D68',
        '#c707af',
        '#875BF7',
        '#444CE7',
        '#444CE8',
        '#0BA5EC',
        '#0BA5ED',
        '#15B79E',
        '#17B26A',
        '#66C61C',
        '#FAC515',
        '#FAC516',
        '#F38744',
        '#A15C07',
        '#854A0E',
        '#98A2B3',
        '#667085',
        '#98A2B4',
        '#D0D5DD',
        '#EAECF0',
        '#DEDEDE',
        '#F7F7F7',
    ];

    const handleSelectedBgColor = () => {
        props.onBgColorSelect(bgColor.hex);
    };

    return (
        <>
            <div className='mt-4 flex items-center justify-center gap-5 md:flex-col'>
                <ColorPicker
                    color={bgColor}
                    onChange={setBgColor}
                    hideInput={['hsv', 'rgb']}
                    height={195}
                    hideAlpha={true}
                />
                {
                    <>
                        <Button
                            variant='secondary'
                            size='lg'
                            className='hidden w-[183px] rounded-button text-[12px] font-[700] text-[#155EEF] md:flex'
                            icon
                            onClick={handleSelectedBgColor}>
                            <ColorIcon />
                            Apply colour
                        </Button>
                        <Button
                            variant='secondary'
                            size='sm'
                            className='flex h-10 rounded-button text-[12px] font-[700] text-[#155EEF] md:hidden'
                            icon
                            onClick={handleSelectedBgColor}>
                            <ColorIcon />
                            Apply colour
                        </Button>
                    </>
                }
            </div>
            <div className='flex items-center'>
                <div className='mt-8 flex flex-col pl-[6px] text-[14px] font-[700] text-[#FFFFFF] md:pl-0'>
                    <span>Presets</span>
                </div>
            </div>
            <div className='mt-4 grid grid-cols-7 items-center justify-center gap-2 md:grid-cols-5'>
                {presetArray.map((color) => (
                    <div
                        key={color}
                        className={`flex h-7 w-7 cursor-pointer rounded-[4px]`}
                        style={{
                            backgroundColor: color,
                            border: color === props.bgColorPalette ? '1px solid white' : 'none',
                        }}
                        onClick={() => {
                            props.onBgColorSelect(color);
                        }}></div>
                ))}
            </div>
        </>
    );
};

export const ImageBgOption = (props: {bgImageUid?: string; onBgImageSelect: (imgUid: string) => void}) => {
    return (
        <>
            <div className='mt-4 grid grid-cols-7 items-center justify-center gap-2 md:grid-cols-3'>
                {GetTextureFiles().map((texture) => (
                    <img
                        key={texture.uid}
                        src={encodeUrl(texture.thumbnail_url)}
                        alt={texture.name}
                        className={`h-[53px] w-[53px] cursor-pointer rounded-[4px] object-cover`}
                        style={{border: texture.uid === props.bgImageUid ? '3px solid grey' : 'none'}}
                        onClick={() => {
                            props.onBgImageSelect(texture.uid);
                        }}></img>
                ))}
            </div>
        </>
    );
};

export const BackgroundSubMenu: React.FC<SubMenuProps> = (props) => {
    let isSvgOrGif = false;
    if (props.fileData && props.fileData.name) {
        const fileName = props.fileData.name;
        isSvgOrGif =
            fileName.toLowerCase().endsWith(`.${MediaType.SVG}`) ||
            fileName.toLowerCase().endsWith(`.${MediaType.SVG_XML}`) ||
            fileName.toLowerCase().endsWith(`.${MediaType.GIF}`);
    }

    const onBgColorSelect = (color: string) => {
        props.trackEvent('BgColorSelected', {});
        props.updateTransformationObject({
            ...props.transformationObject,
            bgImageUid: undefined,
            bgColorPalette: color,
        });
    };

    const onBgImageSelect = (uid: string) => {
        props.trackEvent('BgImageSelected', {bg_img_uid: uid});
        props.updateTransformationObject({
            ...props.transformationObject,
            bgColorPalette: undefined,
            bgImageUid: uid,
        });
    };

    return (
        <div>
            <div className='mb-4'>
                <span className='text-[14px] font-[700] text-[#FFFFFF]'> Replace Background</span>
            </div>
            {!isSvgOrGif ? (
                <Tabs defaultValue='texture'>
                    <TabsList>
                        <TabsTrigger value='texture'>Texture</TabsTrigger>
                        <TabsTrigger value='colour'>Colour</TabsTrigger>
                    </TabsList>
                    <TabsContent value='colour'>
                        <ColorBgOption
                            bgColorPalette={props.transformationObject.bgColorPalette}
                            onBgColorSelect={onBgColorSelect}
                        />
                    </TabsContent>
                    <TabsContent value='texture'>
                        <ImageBgOption
                            bgImageUid={props.transformationObject.bgImageUid}
                            onBgImageSelect={onBgImageSelect}
                        />
                    </TabsContent>
                </Tabs>
            ) : (
                <div className={'flex py-20'}>
                    <span className={'text-inter text-[18px] font-[600] text-white'}>
                        Not supported on the current image format.
                    </span>
                </div>
            )}
        </div>
    );
};
